import type { Formula, FormulaSource } from '@finsim/model';
import { Plus, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button, EditOnly, IconButton, Segmented, Select } from '../../../shared/ui';
import { decodeSource, encodeSource } from '../lib';
import type { FormContext } from './context';

/** The "Based on" list of a formula: signed sources picked from the variant. */
export function SourcesField({
  item,
  ctx,
  onChange,
}: {
  item: Formula;
  ctx: FormContext;
  onChange: (sources: FormulaSource[]) => void;
}) {
  const { t } = useTranslation();
  const scenario = ctx.project.scenarios.find((s) => s.id === ctx.scenarioId);
  const inScenario = (scenario?.itemIds ?? []).flatMap((id) => {
    const i = ctx.project.items[id];
    return i && i.id !== item.id ? [i] : [];
  });

  const options = [
    { value: 'totalIncome', label: t('fields.totalIncome') },
    { value: 'totalExpenses', label: t('fields.totalExpenses') },
    ...inScenario
      .filter((i) => i.type === 'flow' || i.type === 'formula')
      .map((i) => ({ value: `item:${i.id}`, label: i.name })),
    ...inScenario
      .filter((i) => i.type === 'asset')
      .map((i) => ({
        value: `accountReturn:${i.id}`,
        label: t('fields.accountReturn', { name: i.name }),
      })),
    ...ctx.project.categories.map((c) => ({
      value: `category:${c.id}`,
      label: `${t('fields.category')}: ${c.name}`,
    })),
  ];
  const sources = item.spec.sources;

  return (
    <div>
      <p className="mb-1.5 text-xs text-text-3">{t('fields.sources')}</p>
      <ul className="space-y-1.5">
        {sources.map((source, index) => (
          <li
            key={`${encodeSource(source.ref)}-${String(index)}`}
            className="flex items-center gap-1.5"
          >
            <Segmented
              label={t('fields.sources')}
              value={source.sign === 1 ? 'add' : 'subtract'}
              onValueChange={(v) => {
                onChange(
                  sources.map((s, i) => (i === index ? { ...s, sign: v === 'add' ? 1 : -1 } : s)),
                );
              }}
              options={[
                { value: 'add', label: '+', hint: t('fields.add') },
                { value: 'subtract', label: '−', hint: t('fields.subtract') },
              ]}
            />
            <Select
              aria-label={t('fields.sources')}
              value={encodeSource(source.ref)}
              options={
                options.some((o) => o.value === encodeSource(source.ref))
                  ? options
                  : [
                      ...options,
                      { value: encodeSource(source.ref), label: encodeSource(source.ref) },
                    ]
              }
              onValueChange={(v) => {
                onChange(sources.map((s, i) => (i === index ? { ...s, ref: decodeSource(v) } : s)));
              }}
            />
            <EditOnly>
              <IconButton
                label={t('fields.removeSource')}
                size="md"
                onClick={() => {
                  onChange(sources.filter((_, i) => i !== index));
                }}
              >
                <X />
              </IconButton>
            </EditOnly>
          </li>
        ))}
      </ul>
      <EditOnly>
        <Button
          size="sm"
          variant="ghost"
          className="mt-1.5"
          leadingIcon={<Plus />}
          onClick={() => {
            onChange([...sources, { ref: { type: 'totalIncome' }, sign: 1 }]);
          }}
        >
          {t('fields.addSource')}
        </Button>
      </EditOnly>
    </div>
  );
}
